import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./cn";

const buttonVariants = cva(
  // Base styles
  [
    "inline-flex items-center justify-center gap-2",
    "font-medium whitespace-nowrap",
    "transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        default: [
          "bg-accent-primary text-accent-primary-text",
          "hover:bg-accent-primary/90",
        ],
        secondary: [
          "bg-accent-secondary text-accent-secondary-text",
          "hover:bg-accent-secondary/90",
        ],
        olive: [
          "bg-olive text-off-white",
          "hover:bg-olive/90",
        ],
        outline: [
          "border border-border-primary bg-transparent text-text-primary",
          "hover:bg-surface-1",
        ],
        ghost: ["bg-transparent text-text-primary", "hover:bg-surface-1"],
        "ghost-secondary": [
          "bg-surface-1 text-text-secondary",
          "hover:bg-surface-2",
        ],
        destructive: [
          "bg-accent-negative text-white",
          "hover:bg-accent-negative/90",
        ],
        // Inverted - fills with the primary text color, reversed text on top
        inverted: [
          "bg-text-primary text-text-reversed",
          "hover:bg-text-primary/90",
        ],
      },
      size: {
        sm: "h-button-sm px-3 text-sm rounded-lg",
        md: "h-button px-4 text-base rounded-lg",
        lg: "h-button-lg px-6 text-lg rounded-lg",
        // Pill sizes - rounded-full with specific heights
        "pill-sm": "h-[40px] px-3 text-[16px] rounded-full min-w-[88px]",
        "pill-md": "h-[44px] px-4 text-base rounded-full",
        // Wide pill - auto height from vertical padding (~50px) for full-width CTAs
        "pill-lg": "rounded-full text-[18px] font-medium py-[12px] px-6",
        // Icon-only circular button
        "icon-sm": "size-[40px] rounded-full p-0",
        "icon-md": "size-[48px] rounded-full p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * When true, the component will render its children directly,
   * passing all props to the child element.
   * Useful for composing with Link components.
   */
  asChild?: boolean;
}

/**
 * Button component with multiple variants and sizes.
 *
 * @example
 * // Default button
 * <Button>Click me</Button>
 *
 * // With variant and size
 * <Button variant="outline" size="sm">Small Outline</Button>
 *
 * // As a link
 * <Button asChild>
 *   <Link to="/somewhere">Navigate</Link>
 * </Button>
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
